// Keeps login credentials that travel in URLs out of Sentry (DEV-1032).
//
// The values of these query parameters are replaced with [Filtered] wherever
// they appear in a string sent to Sentry, including URL-encoded inside another
// URL (a Referer or a redirect target):
//
//   token, code, state          the SSO callback (/login?code=&state=, legacy ?token=)
//   code_verifier               the PKCE verifier
//   sso_state, sso_challenge    the PKCE values sent to kvasir's /session/user
//   access_token, id_token      OAuth-style tokens, should one ever show up in a URL
//   email                       the login dialog's /login?email= (personal data)
//
// Coverage: error events (beforeSend), transactions and their spans
// (beforeSendTransaction, beforeSendSpan), breadcrumbs (beforeBreadcrumb), the
// replay event with its list of visited URLs (the ScrubAuthParams integration's
// processEvent, which also runs for replay events) and the Replay recording
// itself (beforeAddRecordingEvent). main.tsx also strips the SSO callback
// parameters from the address bar before Sentry starts, so this is the second
// line of defence.

import type { Breadcrumb, Event, Integration, SpanJSON } from '@sentry/types';

export const SCRUBBED_PARAMS = [
  'token',
  'code',
  'state',
  'code_verifier',
  'sso_state',
  'sso_challenge',
  'access_token',
  'id_token',
  'email',
];

export const FILTERED = '[Filtered]';

const names = SCRUBBED_PARAMS.join('|');
// name=value in a URL or query string. The name has to start the string or
// follow ? & # or ; so that e.g. "postcode=" or "status_code=" is left alone.
const plainParam = new RegExp(`((?:^|[?&#;])(?:${names})=)[^&#\\s"'<>]*`, 'gi');
// The same inside a URL-encoded URL: %3Fname%3Dvalue or %26name%3Dvalue.
const encodedParam = new RegExp(
  `((?:%3F|%26|%23)(?:${names})%3D)(?:(?!%26|%23)[^&#\\s"'<>])*`,
  'gi',
);

// Deep enough for Replay snapshots of the DOM; strings below it are kept as is.
const MAX_DEPTH = 64;

/** Replaces the values of the scrubbed parameters in a string. */
export function scrubString(value: string): string {
  if (!value || (value.indexOf('=') === -1 && !/%3D/i.test(value))) {
    return value;
  }
  return value
    .replace(plainParam, `$1${FILTERED}`)
    .replace(encodedParam, `$1${FILTERED}`);
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value) as unknown;
  return proto === Object.prototype || proto === null;
}

/**
 * Returns a copy of value with every string scrubbed. Only arrays and plain
 * objects are walked; anything else is returned as is. An object reached more
 * than once (a shared or cyclic reference) resolves to the same scrubbed copy,
 * so the original, unscrubbed object never ends up in the result.
 */
export function scrubDeep<T>(
  value: T,
  depth = 0,
  seen = new WeakMap<object, unknown>(),
): T {
  if (typeof value === 'string') {
    return scrubString(value) as unknown as T;
  }
  if (depth > MAX_DEPTH || value === null || typeof value !== 'object') {
    return value;
  }
  const original = value as unknown as object;
  if (seen.has(original)) return seen.get(original) as T;

  if (Array.isArray(value)) {
    // Register the copy before recursing so cycles resolve to it.
    const out: unknown[] = [];
    seen.set(original, out);
    for (const item of value as unknown[]) {
      out.push(scrubDeep(item, depth + 1, seen));
    }
    return out as unknown as T;
  }
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    seen.set(original, out);
    for (const key of Object.keys(value)) {
      out[key] = scrubDeep(value[key], depth + 1, seen);
    }
    return out as T;
  }
  return value;
}

function isScrubbedName(name: unknown): boolean {
  return (
    typeof name === 'string' && SCRUBBED_PARAMS.includes(name.toLowerCase())
  );
}

// request.query_string may also be an object or a list of [name, value] pairs.
function scrubQueryString(query: unknown): unknown {
  if (Array.isArray(query)) {
    return query.map((pair: unknown) =>
      Array.isArray(pair) && isScrubbedName(pair[0])
        ? [pair[0], FILTERED]
        : pair,
    );
  }
  if (isPlainObject(query)) {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(query)) {
      out[key] = isScrubbedName(key) ? FILTERED : query[key];
    }
    return out;
  }
  return query;
}

export function scrubEvent<E extends Event>(event: E): E {
  const scrubbed = scrubDeep(event);
  if (scrubbed.request && scrubbed.request.query_string !== undefined) {
    scrubbed.request.query_string = scrubQueryString(
      scrubbed.request.query_string,
    ) as typeof scrubbed.request.query_string;
  }
  return scrubbed;
}

export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  return scrubDeep(breadcrumb);
}

export function scrubSpan(span: SpanJSON): SpanJSON {
  return scrubDeep(span);
}

/** For Replay's beforeAddRecordingEvent (rrweb and Sentry custom events). */
export function scrubRecordingEvent<T>(event: T): T {
  return scrubDeep(event);
}

/**
 * Runs scrubEvent as an event processor. Unlike beforeSend this also covers
 * replay events, whose `urls` list every URL the session visited.
 */
export function scrubAuthParamsIntegration(): Integration {
  return {
    name: 'ScrubAuthParams',
    processEvent: event => scrubEvent(event),
  };
}
